import {
  mapApiError,
  type ServerErrors
} from '@/composables/useAccountFormErrors'
import {
  changeEmail,
  changePassword,
  deleteAccount,
  type ApiRequestError
} from '@/services/api/AccountApi'
import MessageHelper from '@/services/helpers/MessageHelper'
import { useCustomerStore } from '@/stores/customer'
import { ref, type Ref } from 'vue'

export type { ServerErrors }

export interface ProfileFormModel {
  first_name: string
  last_name: string
  gender: string
  title: string | null
  dob: string | null
  phone: string | null
}

export interface PasswordFormModel {
  current_password: string
  new_password: string
  new_password_retype: string
}

export interface EmailFormModel {
  current_password: string
  email: string
}

export interface DeletionFormModel {
  current_password: string
  delete_account: boolean
}

/** Legacy rule: Zend_Validate_StringLength min 8 */
export const MIN_PASSWORD_LENGTH = 8

/**
 * Structural check instead of a pattern: a regex like `[^\s@]+@[^\s@]+\.[^\s@]+` is
 * ambiguous, because the dot is also matched by the character class, so it backtracks
 * super-linearly on long input (Sonar S5852). The API validates the address properly.
 */
export const isEmailAddress = (value: unknown): boolean => {
  if (typeof value !== 'string' || /\s/.test(value)) return false

  const parts = value.split('@')
  if (parts.length !== 2) return false

  const [local, domain] = parts

  return (
    '' !== local &&
    domain.includes('.') &&
    !domain.startsWith('.') &&
    !domain.endsWith('.')
  )
}

export function useAccountDetails() {
  const customerStore = useCustomerStore()

  const savingProfile = ref(false)
  const savingPassword = ref(false)
  const savingEmail = ref(false)
  const deletingAccount = ref(false)

  const profileErrors: Ref<ServerErrors> = ref({})
  const passwordErrors: Ref<ServerErrors> = ref({})
  const emailErrors: Ref<ServerErrors> = ref({})
  const deletionErrors: Ref<ServerErrors> = ref({})

  const saveProfile = async (model: ProfileFormModel): Promise<boolean> => {
    savingProfile.value = true
    profileErrors.value = {}
    try {
      await customerStore.updateCustomerData({
        first_name: model.first_name,
        last_name: model.last_name,
        gender: model.gender,
        title: model.title,
        dob: model.dob,
        phone: model.phone
      })
      // updateCustomerData() refetches unforced, and that is a no-op for a logged-in
      // customer, so the store would keep serving the pre-save copy to the whole app.
      await customerStore.fetchProfile(true)
      void MessageHelper.addMessage('account_details_saved')
      return true
    } catch (error) {
      profileErrors.value = _handleError(error as ApiRequestError)
      return false
    } finally {
      savingProfile.value = false
    }
  }

  const savePassword = async (model: PasswordFormModel): Promise<boolean> => {
    savingPassword.value = true
    passwordErrors.value = {}
    try {
      await changePassword({
        current_password: model.current_password,
        new_password: model.new_password
      })
      void MessageHelper.addMessage('account_details_password_changed')
      return true
    } catch (error) {
      passwordErrors.value = _handleError(error as ApiRequestError)
      return false
    } finally {
      savingPassword.value = false
    }
  }

  const saveEmail = async (model: EmailFormModel): Promise<boolean> => {
    savingEmail.value = true
    emailErrors.value = {}
    try {
      await changeEmail({
        current_password: model.current_password,
        email: model.email
      })
      // The address is the login identity, so every profile copy has to catch up.
      await customerStore.fetchProfile(true)
      void MessageHelper.addMessage('account_details_email_changed')
      return true
    } catch (error) {
      emailErrors.value = _handleError(error as ApiRequestError)
      return false
    } finally {
      savingEmail.value = false
    }
  }

  const confirmAccountDeletion = async (
    model: DeletionFormModel
  ): Promise<boolean> => {
    deletingAccount.value = true
    deletionErrors.value = {}
    try {
      await deleteAccount({
        current_password: model.current_password,
        delete_account: true
      })
      return true
    } catch (error) {
      deletionErrors.value = _handleError(error as ApiRequestError)
      return false
    } finally {
      deletingAccount.value = false
    }
  }

  return {
    savingProfile,
    savingPassword,
    savingEmail,
    deletingAccount,
    profileErrors,
    passwordErrors,
    emailErrors,
    deletionErrors,
    saveProfile,
    savePassword,
    saveEmail,
    confirmAccountDeletion
  }
}

const _handleError = (error: ApiRequestError): ServerErrors =>
  mapApiError(error, 'account_details')
